/**
 * Edit Item Modal — pakket en extra's van een wasserij-item bewerken.
 *
 * WbModal 'sheet': pakket als één-keuze-tegels, huidige extra's als rijen met
 * Verwijder/Ongedaan, extra's toevoegen als meerkeuze-tegels, wijzigingen als
 * WbFlag, Opslaan onderin. Alleen voor "Ophalen"-stops. Save-volgorde en
 * payloads ongewijzigd (pakket, verwijderen, toevoegen).
 */

import { useState, useEffect, useCallback } from 'react';
import { salesforceClient } from '../lib/salesforce';
import { WbButton, WbFlag, WbListRow, WbModal, WbState, WbTile } from './wb';
import type {
  ItemEditOptions,
  PackageProduct,
  ExtraProduct,
  CurrentExtra,
} from '../lib/salesforce';

// Props for the wasserij item being edited
interface WasserijItemDto {
  itemId: string;
  itemName: string;
  productName?: string;
  productCategory?: 'Primary' | 'Extra' | 'Package' | 'Upsell';
  totalPrice?: number;
  totalPriceWithExtras?: number;
  totalPriceWithExtrasAndDiscount?: number;
  discountPercent?: number;
}

interface EditItemModalProps {
  /** The primary item being edited */
  item: WasserijItemDto;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when changes are saved successfully */
  onSaved: () => void;
}

function euro(n: number): string {
  return `€${n.toFixed(2).replace('.', ',')}`;
}

export function EditItemModal({ item, onClose, onSaved }: EditItemModalProps) {
  // Data state
  const [editOptions, setEditOptions] = useState<ItemEditOptions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selection state
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [selectedExtraIds, setSelectedExtraIds] = useState<Set<string>>(new Set());
  const [extrasToRemove, setExtrasToRemove] = useState<Set<string>>(new Set()); // wasserijItemIds

  // Save state
  const [isSaving, setIsSaving] = useState(false);

  // Original state for delta calculation
  const [originalPackageId, setOriginalPackageId] = useState<string | null>(null);
  const [originalExtraIds, setOriginalExtraIds] = useState<Set<string>>(new Set());

  // Load edit options on mount
  useEffect(() => {
    async function loadEditOptions() {
      setIsLoading(true);
      setError(null);

      try {
        const response = await salesforceClient.getItemEditOptions(item.itemId);

        if (response.success && response.data) {
          setEditOptions(response.data);

          // Set initial selections
          const currentPackageId = response.data.currentPackage?.productId || null;
          setSelectedPackageId(currentPackageId);
          setOriginalPackageId(currentPackageId);

          const currentExtraProductIds = new Set(
            response.data.currentExtras.map((e) => e.productId)
          );
          setSelectedExtraIds(currentExtraProductIds);
          setOriginalExtraIds(currentExtraProductIds);
        } else {
          setError(response.message || 'Kon bewerkopties niet laden');
        }
      } catch (err) {
        console.error('Failed to load edit options:', err);
        setError('Kon bewerkopties niet laden. Controleer je internetverbinding.');
      } finally {
        setIsLoading(false);
      }
    }

    loadEditOptions();
  }, [item.itemId]);

  // Check if there are any changes
  const hasChanges = useCallback(() => {
    if (selectedPackageId !== originalPackageId) return true;
    if (extrasToRemove.size > 0) return true;

    // Check for new extras (selected but not in original)
    for (const id of selectedExtraIds) {
      if (!originalExtraIds.has(id)) return true;
    }

    return false;
  }, [selectedPackageId, originalPackageId, selectedExtraIds, originalExtraIds, extrasToRemove]);

  // Toggle extra selection (for adding new ones)
  const toggleExtra = (extraId: string) => {
    setSelectedExtraIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(extraId)) {
        newSet.delete(extraId);
      } else {
        newSet.add(extraId);
      }
      return newSet;
    });
  };

  // Mark a current extra for removal
  const markExtraForRemoval = (wasserijItemId: string, productId: string) => {
    setExtrasToRemove((prev) => new Set([...prev, wasserijItemId]));
    setSelectedExtraIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(productId);
      return newSet;
    });
  };

  // Undo removal of an extra
  const undoExtraRemoval = (wasserijItemId: string, productId: string) => {
    setExtrasToRemove((prev) => {
      const newSet = new Set(prev);
      newSet.delete(wasserijItemId);
      return newSet;
    });
    setSelectedExtraIds((prev) => new Set([...prev, productId]));
  };

  // Save changes
  const handleSave = async () => {
    if (!hasChanges()) {
      onClose();
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // 1. Replace package if changed
      if (selectedPackageId && selectedPackageId !== originalPackageId) {
        const packageResponse = await salesforceClient.replaceItemPackage(
          item.itemId,
          selectedPackageId
        );
        if (!packageResponse.success) {
          throw new Error(packageResponse.message || 'Pakket wijzigen mislukt');
        }
      }

      // 2. Remove marked extras
      for (const wasserijItemId of extrasToRemove) {
        const removeResponse = await salesforceClient.removeItemExtra(
          item.itemId,
          wasserijItemId
        );
        if (!removeResponse.success) {
          console.error('Failed to remove extra:', removeResponse.message);
          // Continue with other removals
        }
      }

      // 3. Add new extras (ones that are selected but weren't originally there)
      const newExtraProductIds = [...selectedExtraIds].filter(
        (id) => !originalExtraIds.has(id)
      );
      if (newExtraProductIds.length > 0) {
        const addResponse = await salesforceClient.addItemExtras(
          item.itemId,
          newExtraProductIds
        );
        if (!addResponse.success) {
          throw new Error(addResponse.message || 'Extra\'s toevoegen mislukt');
        }
      }

      // Success - trigger refresh
      onSaved();
    } catch (err) {
      console.error('Failed to save changes:', err);
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
      setIsSaving(false);
    }
  };

  // Calculate price delta (simplified - just show number of changes)
  const getChangesSummary = () => {
    const changes: string[] = [];

    if (selectedPackageId !== originalPackageId) {
      const newPkg = editOptions?.availablePackages.find(
        (p) => p.productId === selectedPackageId
      );
      if (newPkg) {
        changes.push(`Pakket: ${newPkg.productName}`);
      }
    }

    if (extrasToRemove.size > 0) {
      changes.push(`${extrasToRemove.size} extra(s) verwijderen`);
    }

    const newExtrasCount = [...selectedExtraIds].filter(
      (id) => !originalExtraIds.has(id)
    ).length;
    if (newExtrasCount > 0) {
      changes.push(`${newExtrasCount} extra(s) toevoegen`);
    }

    return changes;
  };

  const gewijzigd = hasChanges();

  return (
    <WbModal
      open
      onClose={onClose}
      variant="sheet"
      titel="Item bewerken"
      subtitel={item.itemName}
      sluitOpAchtergrond={!gewijzigd && !isSaving}
      acties={
        <>
          <WbButton variant="secondary" size="lg" onClick={onClose} disabled={isSaving}>Annuleren</WbButton>
          <WbButton size="lg" onClick={handleSave} disabled={!gewijzigd} isLoading={isSaving}>Opslaan</WbButton>
        </>
      }
    >
      {isLoading ? (
        <WbState variant="laden" titel="Opties laden…" />
      ) : error && !editOptions ? (
        <WbFlag kind="geld">{error}</WbFlag>
      ) : editOptions ? (
        <div className="space-y-4">
          <Sectie titel="Wasbehandeling">
            <div className="grid grid-cols-1 gap-1.5">
              {editOptions.availablePackages.map((pkg: PackageProduct) => (
                <WbTile key={pkg.productId} rol="radio" selected={selectedPackageId === pkg.productId} onToggle={() => setSelectedPackageId(pkg.productId)}>
                  <span className="block">{pkg.productName}</span>
                  {(pkg.description || (pkg.percentage != null && pkg.percentage > 0)) && (
                    <span className="block text-sm font-normal text-wb-inkt2">
                      {pkg.description}{pkg.description && pkg.percentage != null && pkg.percentage > 0 ? ' · ' : ''}
                      {pkg.percentage != null && pkg.percentage > 0 ? <span className="font-mono">+{pkg.percentage}%</span> : null}
                    </span>
                  )}
                </WbTile>
              ))}
            </div>
          </Sectie>

          {editOptions.currentExtras.length > 0 && (
            <Sectie titel="Huidige extra's">
              {editOptions.currentExtras.map((extra: CurrentExtra) => {
                const weg = extrasToRemove.has(extra.wasserijItemId);
                return (
                  <WbListRow
                    key={extra.wasserijItemId}
                    tekst
                    hoofd={<span className={weg ? 'line-through text-wb-inkt2' : undefined}>{extra.productName}</span>}
                    sub={extra.price != null && extra.price > 0 ? euro(extra.price) : undefined}
                    rechts={
                      weg ? (
                        <WbButton variant="link" onClick={() => undoExtraRemoval(extra.wasserijItemId, extra.productId)}>Ongedaan</WbButton>
                      ) : (
                        <WbButton variant="secondary" fullWidth={false} onClick={() => markExtraForRemoval(extra.wasserijItemId, extra.productId)}>Verwijder</WbButton>
                      )
                    }
                  />
                );
              })}
            </Sectie>
          )}

          {editOptions.availableExtras.length > 0 && (
            <Sectie titel="Extra's toevoegen">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {editOptions.availableExtras
                  .filter((extra) => !editOptions.currentExtras.some((ce) => ce.productId === extra.productId))
                  .map((extra: ExtraProduct) => {
                    const isSelected = selectedExtraIds.has(extra.productId);
                    const prijs = extra.pricingType === 'percentage' && extra.percentage != null
                      ? `+${extra.percentage}%`
                      : extra.absolutePrice != null
                        ? euro(extra.absolutePrice)
                        : 'Prijs op aanvraag';
                    return (
                      <WbTile key={extra.productId} selected={isSelected} onToggle={() => toggleExtra(extra.productId)}>
                        <span className="block">{extra.productName}</span>
                        <span className="block text-sm font-normal text-wb-inkt2">
                          {extra.description ? `${extra.description} · ` : ''}<span className="font-mono">{prijs}</span>
                        </span>
                      </WbTile>
                    );
                  })}
              </div>
            </Sectie>
          )}

          {gewijzigd && (
            <WbFlag kind="info" title="Wijzigingen">
              {getChangesSummary().map((c) => <span key={c} className="block">{c}</span>)}
              <span className="block text-sm text-wb-inkt2">Prijzen worden bijgewerkt na opslaan.</span>
            </WbFlag>
          )}
          {error && <WbFlag kind="geld">{error}</WbFlag>}
        </div>
      ) : null}
    </WbModal>
  );
}

function Sectie({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h3 className="font-wb-heading font-bold text-xs uppercase tracking-wide text-wb-inkt2">{titel}</h3>
      {children}
    </section>
  );
}
